import { defineStore } from "pinia";
import { ref } from "vue";
import {
  fetchLogin,
  fetchLogout,
  fetchSignUp,
  fetchUserInfo,
} from "@/api/user";
import {
  fetchCancelSubscription,
  fetchStripeSuccess,
} from "@/api/subscription";
import axios, { AxiosError } from "axios";
import { AuthResponse, AuthUser, SignupPayload } from "@/types/auth";
import { PremiumCalculator, SubscriptionTier } from "@/types/premiumTypes";
import { Subscription } from "@/types/subscription";

export const useUserStore = defineStore("user", () => {
  let userInfoFetchPromise: Promise<void> | null = null;
  let abortController: AbortController | null = null;
  const user = ref<AuthUser | null>(null);

  async function signUp(payload: SignupPayload) {
    const response = await fetchSignUp(payload);

    saveUserAndUpdateStorage(response);
  }

  async function login(email: string, password: string) {
    const response = await fetchLogin(email, password);

    saveUserAndUpdateStorage(response);
  }

  async function updateUserInfo(forceRefresh = false) {
    if (user.value && !forceRefresh && !userInfoFetchPromise) {
      return Promise.resolve();
    }

    if (userInfoFetchPromise && !forceRefresh) {
      return userInfoFetchPromise;
    }

    if (userInfoFetchPromise && forceRefresh) {
      if (abortController) abortController.abort();
      userInfoFetchPromise = null;
    }

    abortController = new AbortController();
    const signal = abortController.signal;

    userInfoFetchPromise = fetchUserInfo(signal)
      .then((authUser: AuthUser) => {
        user.value = authUser;
        user.value.subscriptions = normalizeSubscriptionData(
          authUser.subscriptions
        );
        localStorage.setItem("user", JSON.stringify(authUser));
      })
      .catch((error: AxiosError) => {
        userInfoFetchPromise = null;
        console.error("Update user info error with message:", error);
      })
      .finally(() => {
        userInfoFetchPromise = null;
      });

    return userInfoFetchPromise;
  }

  async function logout() {
    try {
      await fetchLogout();
    } catch (e) {
      if (axios.isAxiosError(e) && e.status === 401) {
        clearUserData();
        return;
      }
    }
    clearUserData();
  }

  async function checkStripeSuccess(sessionId: string) {
    const updatedSubscriptions = await fetchStripeSuccess(sessionId);
    const subscriptions = normalizeSubscriptionData(updatedSubscriptions);
    if (!user.value) {
      loadUserFromStorage();
    }
    if (user.value) {
      user.value.subscriptions = subscriptions;
      localStorage.setItem("user", JSON.stringify(user.value));
    }
  }

  async function cancelSubscription(calculator: PremiumCalculator) {
    if (user.value === null) {
      return;
    }
    const currentSubscription = user.value.subscriptions.find(
      (s: Subscription) => s.calculator === calculator
    );
    if (currentSubscription === undefined) {
      return;
    }
    const res = await fetchCancelSubscription(currentSubscription.id);
    user.value.subscriptions = normalizeSubscriptionData(res);
    localStorage.setItem("user", JSON.stringify(user.value));
  }

  function userHasSubscription(to: PremiumCalculator) {
    if (user.value === null) {
      return false;
    }
    for (const subscription of user.value.subscriptions) {
      if (subscription.calculator === to && subscription.is_active) {
        return true;
      }
    }
    return false;
  }

  function userHasSubscriptionWithTier(
    to: PremiumCalculator,
    tier: SubscriptionTier
  ) {
    if (user.value === null) {
      return false;
    }
    for (const subscription of user.value.subscriptions) {
      if (
        subscription.calculator === to &&
        subscription.is_active &&
        subscription.tiers.includes(tier)
      ) {
        return true;
      }
    }
    return false;
  }

  function loadUserFromStorage() {
    const userRaw = localStorage.getItem("user");
    const jsonUser = userRaw ? JSON.parse(userRaw) : null;
    if (jsonUser === null || jsonUser === undefined) {
      return;
    }
    jsonUser.subscriptions = normalizeSubscriptionData(jsonUser.subscriptions);
    user.value = jsonUser;
  }

  function saveUserAndUpdateStorage(response: AuthResponse) {
    user.value = response.user;
    user.value.subscriptions = normalizeSubscriptionData(
      response.user.subscriptions
    );

    localStorage.setItem("token", response.token);
    localStorage.setItem("user", JSON.stringify(response.user));
  }

  function clearUserData() {
    user.value = null;
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  }

  function normalizeSubscriptionData(subscriptions: Subscription[]) {
    return subscriptions.map((subscription) => ({
      ...subscription,
      period_end: subscription.period_end
        ? new Date(subscription.period_end)
        : null,
      ends_at: subscription.ends_at ? new Date(subscription.ends_at) : null,
    }));
  }

  return {
    user,
    signUp,
    login,
    updateUserInfo,
    logout,
    loadUserFromStorage,
    cancelSubscription,
    checkStripeSuccess,
    userHasSubscription,
    userHasSubscriptionWithTier,
  };
});
